import { addCompoundOperation } from '../../../../../../offline/offlineCompoundQueue';
import { captureLocalImages } from '../../../../../../offline/offlineImageStore';
import {
  directoryNaming,
  formActionNaming,
} from '../../../../../../components/forms/ActionForms/constants';
import { isBasicAction } from '../../../../../../utils/order';

export type Phase2Draft = Record<string, any>;

/**
 * A dónde vuelve el operario al encolar la fase 2.
 *
 * 🔴 La regla, corregida el 2026-09-12 con lo visto en carretera: **encolar no
 * cambia a dónde aterrizas**. Con red, terminar el asistente te deja en el
 * RESUMEN de la actuación (`../../summary/{actionId}`), y sin red tiene que
 * dejarte en el mismo sitio. Antes te soltaba en el padre, así que el mismo
 * gesto acababa en pantallas distintas según la cobertura.
 *
 * La excepción no es un capricho: si la actuación **todavía no existe** (fase 1
 * sin red, modo `create`), no hay resumen que abrir —no hay id— y lo único
 * sensato es volver al padre. Lo mismo vale para la básica, que vuelve a su
 * recorrido.
 *
 * `routeId` puede faltar si el borrador lo sembró una versión anterior y
 * sobrevivió a una actualización OTA a mitad de asistente; sin el guard el
 * destino sería `/surveillance-route/undefined/child-orders`.
 */
export function phase2QueuedRedirect(draft: Phase2Draft): string {
  if (isBasicAction({ containerType: draft.containerType }) && draft.routeId) {
    return `/surveillance-route/${draft.routeId}/child-orders`;
  }
  // La actuación ya existe (fase 1 sí llegó): al resumen, igual que con red.
  // La ruta va RELATIVA, exactamente como el `return redirect()` del camino
  // feliz: se resuelve desde la misma pantalla, así que no hay que reconstruir
  // el prefijo (`/work-orders` o `/campaigns`) ni arriesgarse a un 404.
  if (draft.actionId) return `../../summary/${draft.actionId}`;
  // Sin id no hay resumen que abrir: al padre.
  return `${draft.isCampaignParent ? '/campaigns' : '/work-orders'}/${draft.orderParentId}`;
}

/**
 * Encola la fase 2 entera de la actuación (snapshot del asistente + fotos de
 * OPFS) en la cola compound y limpia el borrador. Devuelve el destino.
 *
 * - `create`: la actuación no existe (fase 1 sin red). El replay la crea; si la
 *   creación se intentó con red y se abortó, lleva su `createClientOpId` para
 *   que el backend devuelva lo ya creado en vez de duplicar (D3).
 * - `existing`: la actuación YA existe (fase 1 con red, fase 2 sin llegar). El
 *   replay solo actualiza por estos ids (D2). Sin este campo el replay crearía
 *   una segunda actuación — el duplicado del spec § 0.2.b. El tramo no viaja:
 *   ya se vinculó en la fase 1.
 *
 * Spec: docs/superpowers/specs/2026-09-08-fase2-encolada-red-degradada-design.md
 */
export async function queueActionPhase2({
  mode,
  draft,
}: {
  mode: 'create' | 'existing';
  draft: Phase2Draft;
}): Promise<{ redirectTo: string }> {
  const sessionSnapshots: Record<string, string | null> = {};
  Object.values(formActionNaming).forEach((key) => {
    sessionSnapshots[key] = sessionStorage.getItem(key);
  });
  const localImages = await captureLocalImages([directoryNaming.CANVAS, 'action']);

  await addCompoundOperation({
    type: 'action',
    parentOrderId: draft.orderParentId as string,
    parentOrderRoid: draft.orderParentRoid as string,
    isCampaignParent: !!draft.isCampaignParent,
    sessionSnapshots,
    localImages,
    fromSegmentId: mode === 'create' ? draft.fromSegmentId ?? null : null,
    ...(mode === 'create' && draft.createClientOpId
      ? { createClientOpId: String(draft.createClientOpId) }
      : {}),
    ...(mode === 'existing'
      ? { existing: { orderId: String(draft.orderId), entityId: String(draft.actionId) } }
      : {}),
  });

  Object.values(formActionNaming).forEach((key) => sessionStorage.removeItem(key));

  return { redirectTo: phase2QueuedRedirect(draft) };
}
